/**
 * ParticipantList: 참여자 목록 패널.
 *
 * v1 원본: RenewalComponents/SVContent/Sidebar/UserList/UserListPresenter.js
 *   <div className="new_chat typeMemberList">
 *     <div className="chat_tabs">
 *       <div className="chat_selection"><h2>참여자 <em>{N}</em></h2></div>
 *       <div className="right_btns">
 *         <button className="memberList__btn iconInvite">초대</button>
 *         <button className="chat_closeBtn">닫기</button>
 *       </div>
 *     </div>
 *     <div className="rtcNew_joinMemberListWrap scrollCustom">
 *       <ul className="rtcNew_joinMemberList">...</ul>
 *     </div>
 *   </div>
 *
 * CSS 셀렉터: .rtc_new3 .sidebar.new2 .new_chat.typeMemberList ...
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { shallowEqual } from 'react-redux';
import { localActions } from '@/store/slices/localSlice';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import { participantsActions, type Participant } from '@/store/slices/participantsSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { isMasterUser } from '@/store/slices/masterSlice';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { toastActions } from '@/store/slices/toastSlice';
import LUXDialog from '@/components/Dialog';
import SNNameCard, { ChatButton, MailButton, MessageButton } from '@luna-sena/sena-namecard';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import { inviteAPI } from '@/api/inviteAPI';
import { apiCore } from '@/api/apiCore';
import apiConfig from '@/api/config';
import apiManager from '@/api';
import { getSyncedNow } from '@/utils/timeOffset';

interface ParticipantItemProps {
  participant: Participant | 'local';
  localUserId: string;
  isMainUser: boolean;
  onSelectMain: (userId: string) => void;
  onKickParticipant?: (userId: string) => void;
  onGrantMaster?: (userId: string) => boolean | Promise<boolean>;
  onClearMaster?: (userId: string) => boolean | Promise<boolean>;
  isLocalMaster: boolean;
  onToggleAudio?: () => void;
  onToggleVideo?: () => void;
}

const MAX_PIN_COUNT = 2;

const normalizeUserNo = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim();
  return normalized && normalized !== '0' ? normalized : '';
};

const getParticipantUserNo = (participant?: Participant | null): string => (
  normalizeUserNo(participant?.userNo)
  || normalizeUserNo(participant?.properties?.userNo)
  || normalizeUserNo(participant?.properties?.user_no)
);

import { toPinKey, parsePinKey } from '@/utils/pinUtils';
import { toAbsoluteProfileUrl, toDisplayProfileUrl, DEFAULT_PROFILE } from '@/utils/profileUtils';
import { formatPhoneNumber } from '@/utils/formatUtils';
import { setCookie } from '@/utils/cookie';
import { getShareUrl } from '@/utils/urlUtils';

/** 직원 프로필 캐시 (세션 내 재요청 방지) */
const profileCache = new Map<string, any>();

/**
 * ParticipantItem: v1 JoinedUserPresenter.js 마크업 기반
 *
 * <li className="rtcNew_joinMemberList__item">
 *   .profileBox > .profileBox__btn > .common_coProfileBox > .common_coProfileBox__imgBox
 *   .nameBox > .nameBox__icon.master + .nameBox__text > .ellipsis
 *   .btnBox > .btnBox__btn.iconCam/.iconMic + .funcBox > .btnBox__btn.iconFunc
 * </li>
 */
const ParticipantItem = React.memo<ParticipantItemProps>(({
  participant,
  localUserId,
  isMainUser,
  onSelectMain,
  onKickParticipant,
  onGrantMaster,
  onClearMaster,
  isLocalMaster,
  onToggleAudio,
  onToggleVideo,
}) => {
  const localData = useAppSelector((s) => ({
    userName: s.local.userName,
    userId: s.local.userId,
    profileUrl: s.local.profileUrl,
    avatarUrl: s.local.avatarUrl,
    wehagoId: s.local.wehagoId,
    audioMuted: s.local.audioMuted,
    videoMuted: s.local.videoMuted,
    userNo: s.local.userNo,
    isGuest: s.local.isGuest,
    companyNo: s.local.companyNo,
    nickname: s.local.nickname,
  }), shallowEqual);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const [isOpenMore, setIsOpenMore] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [nameCardData, setNameCardData] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const liRef = useRef<HTMLLIElement>(null);
  const profileBoxRef = useRef<HTMLDivElement>(null);

  const isLocal = participant === 'local';
  const p = isLocal ? null : (participant as Participant);

  const displayName = isLocal
    ? (localData.userName || localData.userId)
    : (p!.displayName || p!.nickname);
  const avatarUrl = isLocal
    ? toDisplayProfileUrl(localData.profileUrl, localData.avatarUrl)
    : toDisplayProfileUrl(p!.profileUrl, p!.avatarUrl);
  const isParticipantModerator = isLocal
    ? isMasterUser(masterUsers, localData.wehagoId)
    : isMasterUser(masterUsers, p!.wehagoId);
  const dispatch = useAppDispatch();
  const localMutedIds = useAppSelector((s) => s.participants.localMutedIds);
  const isAudioMuted = isLocal ? localData.audioMuted : p!.tracks.audioMuted;
  const isVideoMuted = isLocal ? localData.videoMuted : p!.tracks.videoMuted;
  const isLocallyMuted = !isLocal && localMutedIds.includes(p!.id);

  const openMore = useCallback(() => setIsOpenMore(true), []);
  const closeMore = useCallback(() => setIsOpenMore(false), []);

  // 네임카드 대상의 userNo 판별
  const targetUserNo = isLocal ? normalizeUserNo(localData.userNo) : getParticipantUserNo(p);
  const targetName = isLocal ? (localData.userName || localData.nickname) : (p?.displayName || p?.nickname || '');
  const isMember = !!targetUserNo;
  const isLocalGuest = localData.isGuest;

  // 네임카드 열기: 회원이면서 로컬유저가 게스트가 아닌 경우에만
  const handleProfileClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isMember || isLocalGuest) return;

    if (isProfileOpen) {
      setIsProfileOpen(false);
      return;
    }

    setIsProfileOpen(true);

    // 캐시 확인
    if (profileCache.has(targetUserNo!)) {
      setNameCardData(profileCache.get(targetUserNo!));
      return;
    }

    setIsLoadingProfile(true);
    try {
      const cno = apiCore.hSelectedCno || localData.companyNo;
      if (!cno) return;
      const userInfo = await inviteAPI.getUserInfo(cno, targetUserNo!);
      if (userInfo) {
        const data = {
          imageUrl: toAbsoluteProfileUrl(userInfo.profile_url) || avatarUrl,
          name: `${userInfo.user_name || targetName}${userInfo.rank_name ? ' ' + userInfo.rank_name : ''}`,
          position: userInfo.rank_name || undefined,
          organization: userInfo.full_path || undefined,
          email: userInfo.user_default_email || userInfo.user_email || undefined,
          tel: formatPhoneNumber(userInfo.employee_contact) || undefined,
          mobile: formatPhoneNumber(userInfo.user_contact) || undefined,
          isState: '0' as const,
          _raw: {
            company_no: userInfo.company_no,
            employee_no: userInfo.employee_no,
            organization_no: userInfo.organization_no,
            portal_id: userInfo.portal_id,
            user_name: userInfo.user_name,
            user_no: userInfo.user_no,
          },
        };
        profileCache.set(targetUserNo!, data);
        setNameCardData(data);
      }
    } catch {
      // API 오류 시 최소 정보만 표시
      const fallback = {
        imageUrl: avatarUrl,
        name: targetName,
        isState: '0' as const,
      };
      setNameCardData(fallback);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [isMember, isLocalGuest, isProfileOpen, targetUserNo, targetName, avatarUrl, localData.companyNo]);

  // 프로필 카드 외부 클릭 시 닫기
  useEffect(() => {
    if (!isProfileOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (profileBoxRef.current && !profileBoxRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  // ─── 화면 고정 상태 ──────────────────────────────────────────────
  const mainUser = useAppSelector((s) => s.mainUser);
  const pinKey = isLocal ? localUserId : p!.id;
  const isMasterPinned = mainUser.masterPinnedUserIds.includes(pinKey);
  const isUserPinned = mainUser.pinnedUserIds.includes(pinKey);
  const isPinned = isMasterPinned || isUserPinned;
  const baseParticipantName = isLocal
    ? (localData.userName || localData.nickname || '')
    : (p?.displayName || p?.nickname || '');

  // ─── 핸들러 ──────────────────────────────────────────────────────
  const showSnackbar = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    dispatch(toastActions.showSnackbar({ type, message }));
  }, [dispatch]);

  const userId = isLocal ? localUserId : p!.id;

  const handleClickMain = useCallback(() => {
    onSelectMain(userId);
  }, [onSelectMain, userId]);

  const handleKick = useCallback(() => {
    if (onKickParticipant) {
      LUXDialog.confirm(`${displayName} 님을 내보내시겠습니까?`, {
        type: 'warning',
        callback: async () => {
          onKickParticipant?.(userId);
          setIsOpenMore(false);
        },
      });
    }
  }, [onKickParticipant, userId, displayName]);

  const handleMuteRemoteAudio = useCallback(() => {
    getConferenceManager()?.muteRemoteAudio(userId);
    dispatch(toastActions.showSnackbar({ type: 'info', message: `${baseParticipantName}님의 마이크를 껐습니다.` }));
  }, [userId, baseParticipantName, dispatch]);

  const handleMuteRemoteVideo = useCallback(() => {
    getConferenceManager()?.muteRemoteVideo(userId);
    dispatch(toastActions.showSnackbar({ type: 'info', message: `${baseParticipantName}님의 카메라를 껐습니다.` }));
  }, [userId, baseParticipantName, dispatch]);

  const handleRequestUnmute = useCallback((mediaType: 'audio' | 'video') => {
    getConferenceManager()?.requestUnmute(userId, mediaType);
    dispatch(toastActions.showSnackbar({ type: 'info', message: mediaType === 'video' ? '카메라 켜기 요청을 보냈습니다.' : '마이크 켜기 요청을 보냈습니다.' }));
  }, [userId, dispatch]);

  /** 마스터 핀 고정/해제 (브로드캐스트) */
  const doMasterPin = useCallback((type: 'pin' | 'unpin') => {
    const currentMasterPinned = mainUser.masterPinnedUserIds;
    let nextPinned: string[];

    if (type === 'pin') {
      if (currentMasterPinned.includes(pinKey)) return;
      nextPinned = [...currentMasterPinned, pinKey].slice(-MAX_PIN_COUNT);
    } else {
      nextPinned = currentMasterPinned.filter((id) => id !== pinKey);
    }

    if (nextPinned.length > MAX_PIN_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PIN_COUNT}개까지 고정가능합니다.`);
      return;
    }

    dispatch(mainUserActions.setMasterPinnedUsers(nextPinned));

    const room = getConferenceManager()?.getRoom?.();
    if (room) {
      const jitsiId = room.myUserId();
      const broadcastPinned = nextPinned.map(key => {
        const { userId: uid, isScreenShare: ss } = parsePinKey(key);
        return toPinKey(uid === localData.userId ? jitsiId : uid, ss);
      });
      room.sendCommand(CONFERENCE_COMMANDS.UPDATE_MASTER_PIN_FIXED, {
        value: jitsiId,
        attributes: {
          masterPinnedUsers: JSON.stringify(broadcastPinned),
          userName: baseParticipantName,
          type,
          isScreenShare: 'false',
          actionTimestamp: getSyncedNow().toString(),
        },
      });
    }

    if (type === 'pin') {
      dispatch(uiActions.setScreenMode('PRESENTER'));
      showSnackbar('info', `화면이 고정되었습니다. ${nextPinned.length}/${MAX_PIN_COUNT}`);
    } else {
      showSnackbar('info', `화면 고정이 해제되었습니다. ${nextPinned.length}/${MAX_PIN_COUNT}`);
    }
  }, [dispatch, pinKey, baseParticipantName, mainUser.masterPinnedUserIds, showSnackbar, localData.userId]);

  /** 개인 핀 고정/해제 (로컬만) */
  const doUserPin = useCallback((type: 'pin' | 'unpin') => {
    const currentPinned = mainUser.pinnedUserIds;
    const masterCount = mainUser.masterPinnedUserIds.length;

    if (type === 'unpin') {
      if (isMasterPinned) {
        showSnackbar('error', '마스터가 고정한 화면입니다.');
        return;
      }
      const nextPinned = currentPinned.filter((id) => id !== pinKey);
      dispatch(mainUserActions.setPinnedUsers(nextPinned));
      const totalPinned = masterCount + nextPinned.length;
      showSnackbar('info', `화면 고정이 해제되었습니다. ${totalPinned}/${MAX_PIN_COUNT}`);
      return;
    }

    if (currentPinned.includes(pinKey)) return;
    const nextPinned = [...currentPinned, pinKey].slice(-MAX_PIN_COUNT);
    const totalPinned = masterCount + nextPinned.length;

    if (totalPinned > MAX_PIN_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PIN_COUNT}개까지 고정가능합니다.`);
      return;
    }

    dispatch(mainUserActions.setPinnedUsers(nextPinned));
    dispatch(uiActions.setScreenMode('PRESENTER'));
    showSnackbar('info', `화면이 고정되었습니다. ${totalPinned}/${MAX_PIN_COUNT}`);
  }, [dispatch, pinKey, isMasterPinned, mainUser.pinnedUserIds, mainUser.masterPinnedUserIds, showSnackbar]);

  /** 화면 고정/해제 핸들러 (v1 ContextMenu handlePin 패턴) */
  const handlePin = useCallback(() => {
    const allPinned = new Set([
      ...mainUser.masterPinnedUserIds,
      ...mainUser.pinnedUserIds,
      ...(isPinned ? [] : [pinKey]),
    ]);

    if (!isPinned && allPinned.size > MAX_PIN_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PIN_COUNT}개까지 고정가능합니다.`);
      return;
    }

    const type = isPinned ? 'unpin' : 'pin';

    if (isLocalMaster) {
      if (type === 'pin' && !isMasterPinned) {
        LUXDialog.confirm(`${baseParticipantName}님을\n모두에게 화면 고정 하시겠습니까?`, {
          type: 'info',
          title: '화면 고정',
          confirmButtonLabel: '모두에게 화면 고정',
          cancelButtonLabel: '나에게만 화면 고정',
          confirmCallback: () => doMasterPin('pin'),
          cancelCallback: () => doUserPin('pin'),
        });
      } else if (isMasterPinned) {
        doMasterPin('unpin');
      } else {
        doUserPin(type);
      }
    } else {
      doUserPin(type);
    }
  }, [isPinned, isMasterPinned, isLocalMaster, pinKey, baseParticipantName, mainUser, doMasterPin, doUserPin, showSnackbar]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    if (!isOpenMore) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (liRef.current && !liRef.current.contains(e.target as Node)) {
        setIsOpenMore(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpenMore]);

  // ─── 더보기 옵션 빌드 (기획서 기준) ────────────────────────────────
  const moreOptions: Array<{ text: string; fn: () => void | Promise<void> }> = [];

  if (isLocalMaster && isLocal) {
    // ── 마스터 본인 더보기 ──
    moreOptions.push({
      text: isVideoMuted ? '카메라 켜기' : '카메라 끄기',
      fn: () => { onToggleVideo?.(); closeMore(); },
    });
    moreOptions.push({
      text: isAudioMuted ? '마이크 켜기' : '마이크 끄기',
      fn: () => { onToggleAudio?.(); closeMore(); },
    });
    moreOptions.push({
      text: isPinned ? '화면 고정 해제' : '화면 고정',
      fn: () => { handlePin(); closeMore(); },
    });
  } else if (isLocalMaster && !isLocal) {
    // ── 마스터가 다른 참석자 더보기 ──
    // 카메라 제어
    if (!isVideoMuted) {
      moreOptions.push({ text: '카메라 끄기', fn: () => { handleMuteRemoteVideo(); closeMore(); } });
    } else {
      moreOptions.push({ text: '카메라 켜기 요청', fn: () => { handleRequestUnmute('video'); closeMore(); } });
    }
    // 마이크 제어
    if (!isAudioMuted) {
      moreOptions.push({ text: '마이크 끄기', fn: () => { handleMuteRemoteAudio(); closeMore(); } });
    } else {
      moreOptions.push({ text: '마이크 켜기 요청', fn: () => { handleRequestUnmute('audio'); closeMore(); } });
    }
    // 마스터 권한
    if (isParticipantModerator) {
      moreOptions.push({
        text: '마스터 권한 해제',
        fn: async () => {
          const success = await onClearMaster?.(userId);
          if (success) {
            showSnackbar('success', `${baseParticipantName}님의 마스터 권한을 해제하였습니다.`);
          }
          closeMore();
        },
      });
    } else {
      moreOptions.push({
        text: '마스터 권한 부여',
        fn: async () => {
          if (!isMember) {
            showSnackbar('error', '마스터 권한은 회원에게만 부여할 수 있습니다.');
          } else {
            const success = await onGrantMaster?.(userId);
            if (success) {
              showSnackbar('success', `${baseParticipantName}님께 마스터 권한을 부여하였습니다.`);
            }
          }
          closeMore();
        },
      });
    }
    // 화면 고정
    moreOptions.push({
      text: isPinned ? '화면 고정 해제' : '화면 고정',
      fn: () => { handlePin(); closeMore(); },
    });
    // 내보내기
    moreOptions.push({ text: '내보내기', fn: handleKick });
  }


  return (
    <li
      ref={liRef}
      className="rtcNew_joinMemberList__item"
      onClick={handleClickMain}
      onMouseLeave={() => { if (isOpenMore) closeMore(); }}
    >
      {/* 프로필 이미지 + 네임카드 */}
      <div ref={profileBoxRef} className={`profileBox${isProfileOpen ? ' open' : ''}`}>
        <button type="button" className="profileBox__btn" onClick={handleProfileClick}>
          {avatarUrl ? (
            <div className="common_coProfileBox">
              <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url(${avatarUrl})` }} />
            </div>
          ) : (
            <div className="common_coProfileBox common_coProfileBox--icon" style={{ background: '#c1cbdd' }}>
              <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url(${DEFAULT_PROFILE})` }} />
            </div>
          )}
        </button>
        {isProfileOpen && isMember && !isLocalGuest && nameCardData && (
          <SNNameCard
            data={nameCardData}
            buttonElement={[
              <MessageButton key="msg" onClick={() => {
                const raw = nameCardData._raw;
                if (raw) {
                  setCookie('wetalk_message_to_user', JSON.stringify([{
                    company_no: raw.company_no,
                    employee_no: raw.employee_no,
                    organization_no: raw.organization_no,
                    portal_id: raw.portal_id,
                    user_name: raw.user_name,
                    user_no: raw.user_no,
                  }]));
                }
                window.open(`${apiConfig.portalBaseUrl}/#/communication2/message/write?from=og`, '_blank');
              }} />,
              <ChatButton key="chat" onClick={() => {
                const raw = nameCardData._raw;
                if (raw) {
                  setCookie('wetalk_talk_to_user', JSON.stringify([{
                    company_no: raw.company_no,
                    employee_no: raw.employee_no,
                    organization_no: raw.organization_no,
                    portal_id: raw.portal_id,
                    user_name: raw.user_name,
                    user_no: raw.user_no,
                  }]));
                }
                window.open(`${apiConfig.portalBaseUrl}/#/communication2/talk?from=og`, '_blank');
              }} />,
              <MailButton key="mail" onClick={() => {
                const email = nameCardData.email;
                if (email) {
                  window.open(`${apiConfig.portalBaseUrl}/mail/#/mail/compose?to=${encodeURIComponent(email)}&isFromCard=true`, '_blank');
                }
              }} />,
            ]}
          />
        )}
      </div>

      {/* 이름 영역 */}
      <div className="nameBox">
        {isParticipantModerator && (
          <span className="nameBox__icon master">
            <span className="blind">MASTER</span>
          </span>
        )}
        <p className="nameBox__text ellipsis ">
          {/* <span className="ellipsis">{displayName}</span> */}
          {displayName}
          {isLocal && <span> (나)</span>}
        </p>
      </div>

      {/* 버튼 영역 */}
      <div className="btnBox">
        {/* 카메라 버튼 */}
        <button
          type="button"
          className={`btnBox__btn iconCam${isVideoMuted ? ' off' : ''}`}
          style={!isLocal && !isLocalMaster ? { cursor: 'default' } : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (isLocal) {
              onToggleVideo?.();
            } else if (isLocalMaster) {
              if (!isVideoMuted) {
                handleMuteRemoteVideo();
              } else {
                handleRequestUnmute('video');
              }
            }
          }}
        >
          <span className="btnBox__btnTooltip rev">카메라</span>
        </button>
        {/* 마이크 버튼 */}
        <button
          type="button"
          className={`btnBox__btn iconMic${isAudioMuted || isLocallyMuted ? ' off' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isLocal) {
              onToggleAudio?.();
            } else if (isLocalMaster) {
              if (!isAudioMuted) {
                handleMuteRemoteAudio();
              } else {
                handleRequestUnmute('audio');
              }
            } else {
              dispatch(participantsActions.toggleLocalMuted(p!.id));
              dispatch(toastActions.showSnackbar({
                type: 'info',
                message: isLocallyMuted
                  ? `${p!.displayName || p!.nickname}님의 소리를 다시 듣습니다.`
                  : `${p!.displayName || p!.nickname}님의 소리를 껐습니다.`,
              }));
            }
          }}
        >
          <span className="btnBox__btnTooltip rev">{isLocallyMuted ? '소리 켜기' : '마이크'}</span>
        </button>

        {/* 더보기 (원격 참여자 + 마스터만) */}
        {moreOptions.length > 0 && (
          <div className={`funcBox${isOpenMore ? ' open' : ''}`}>
            <button
              type="button"
              className="btnBox__btn iconFunc"
              onClick={(e) => { e.stopPropagation(); isOpenMore ? closeMore() : openMore(); }}
            >
              <span className="btnBox__btnTooltip rev">더보기</span>
            </button>
            <div className="viewerHeader__layer">
              <ul className="viewerHeader__layerList">
                {moreOptions.map((opt, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className="viewerHeader__layerListBtn"
                      onClick={(e) => { e.stopPropagation(); opt.fn(); }}
                    >
                      {opt.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      <button type="button" className="rtcNew_joinMemberList__itemSelector">
        <span className="blind">선택</span>
      </button>
    </li>
  );
});

// ─── 초대된 참석자 (미참석) 타입 ─────────────────────────────────────────

interface InvitedParticipant {
  accessUserNo: number;
  type: 'USER' | 'PHONE' | 'EMAIL';
  cno: number | null;
  userNo: number | null;
  portalId: string | null;
  phone: string | null;
  email: string | null;
  userName: string | null;
  isMaster: boolean;
  profileUrl: string | null;
}

/**
 * InvitedParticipantItem: 초대되었으나 미참석인 참석자 아이템.
 *
 * 마크업 참고:
 * - 프로필: opacity 0.5
 * - USER 타입: 이름 노출
 * - PHONE/EMAIL 타입: 마스킹된 전화번호/이메일 노출
 * - 알림 재전송 버튼 or "참여 알림 전송" 텍스트
 */
const InvitedParticipantItem: React.FC<{
  participant: InvitedParticipant;
  onResend: (accessUserNo: number) => void;
  resendingSet: Set<number>;
  resentSet: Set<number>;
}> = ({ participant, onResend, resendingSet, resentSet }) => {
  const profileUrl = participant.profileUrl
    ? toAbsoluteProfileUrl(participant.profileUrl)
    : undefined;
  const isUser = participant.type === 'USER';
  const displayName = isUser
    ? (participant.userName || participant.portalId || '참석자')
    : participant.type === 'PHONE'
      ? (participant.phone || '전화번호 미등록')
      : (participant.email || '이메일 미등록');

  const alreadySent = resentSet.has(participant.accessUserNo);
  const isResending = resendingSet.has(participant.accessUserNo);

  return (
    <li className="rtcNew_joinMemberList__item">
      {/* 프로필 이미지 (반투명) */}
      <div className="profileBox">
        <button type="button" className="profileBox__btn">
          {profileUrl ? (
            <div className="common_coProfileBox" style={{ opacity: 0.5 }}>
              <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url(${profileUrl})` }} />
            </div>
          ) : (
            <div className="common_coProfileBox common_coProfileBox--icon" style={{ background: '#c1cbdd', opacity: 0.5 }}>
              <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url(${DEFAULT_PROFILE})` }} />
            </div>
          )}
        </button>
      </div>

      {/* 이름 영역 */}
      <div className="nameBox">
        <p className="nameBox__text ellipsis">{displayName}</p>
      </div>

      {/* 버튼 영역: 재전송 버튼 or 알림 전송 완료 텍스트 */}
      <div className="btnBox">
        {alreadySent ? (
          <p className="btnBox__alramText">참여 알림 전송</p>
        ) : (
          <button
            type="button"
            className="btnBox__btn iconMsg"
            disabled={isResending}
            onClick={(e) => {
              e.stopPropagation();
              if (!isResending) onResend(participant.accessUserNo);
            }}
          >
            <span className="btnBox__btnTooltip">재전송</span>
          </button>
        )}
      </div>

      <button type="button" className="rtcNew_joinMemberList__itemSelector">
        <span className="blind">선택</span>
      </button>
    </li>
  );
};

interface ParticipantListProps {
  localUserId: string;
  maxParticipants?: number;
  onKickParticipant?: (userId: string) => void;
  onGrantMaster?: (userId: string) => boolean | Promise<boolean>;
  onClearMaster?: (userId: string) => boolean | Promise<boolean>;
  onToggleAudio?: () => void;
  onToggleVideo?: () => void;
  onResponseJoinRequest?: (userId: string, status: 'allow' | 'deny') => void;
}

const ParticipantList: React.FC<ParticipantListProps> = ({
  localUserId,
  maxParticipants = 50,
  onKickParticipant,
  onGrantMaster,
  onClearMaster,
  onToggleAudio,
  onToggleVideo,
  onResponseJoinRequest,
}) => {
  const dispatch = useAppDispatch();
  const participants = useAppSelector((s) => s.participants);
  const mainUser = useAppSelector((s) => s.mainUser);
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const screenSort = useAppSelector((s) => s.ui.screenSort);
  const speakerOrder = useAppSelector((s) => s.audioIndicator.speakerOrder);
  // NOTE: ParticipantList 메인 컴포넌트는 local 슬라이스의 많은 필드를 다양하게 사용하므로
  // 전체 구독을 유지합니다. ParticipantItem은 이미 세분화되어 있습니다.
  const isLocalMaster = isMasterUser(masterUsers, local.wehagoId);
  const requestJoinUsers = useAppSelector((s) => s.master.requestJoinUserList);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const settingRef = useRef<HTMLDivElement>(null);
  // 게스트 전용: 초대 버튼 클릭 시 회의정보 팝오버
  const [isGuestInviteInfoOpen, setIsGuestInviteInfoOpen] = useState(false);
  const guestInviteRef = useRef<HTMLDivElement>(null);

  // ─── 초대된 참석자 (미참석) 상태 ─────────────────────────────────────
  const [invitedParticipants, setInvitedParticipants] = useState<InvitedParticipant[]>([]);
  const [resentSet, setResentSet] = useState<Set<number>>(new Set());
  const [resendingSet, setResendingSet] = useState<Set<number>>(new Set());

  // ─── 검색 상태 ──────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');

  // 게스트 회의정보 팝오버 외부 클릭 시 닫기
  useEffect(() => {
    if (!isGuestInviteInfoOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (guestInviteRef.current && !guestInviteRef.current.contains(e.target as Node)) {
        setIsGuestInviteInfoOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isGuestInviteInfoOpen]);

  // 회의 ID 포맷: 123456789 → 123-456-789
  const formatConferenceId = useCallback((id: string): string =>
    id ? id.replace(/(\d{3})(?=\d)/g, '$1-') : '', []);

  const hasPassword = local.securityType === 'PASSWORD';
  const conferenceTitle = local.roomName || '회의실';

  // 회의 정보 복사 (ContentHeader와 동일 패턴)
  const handleCopyConferenceInfo = useCallback(async () => {
    let copyMsg = `회의명 : ${conferenceTitle}`;
    copyMsg += `\n링크 : ${getShareUrl()}`;
    if (local.conferenceId) {
      copyMsg += `\n회의 ID : ${formatConferenceId(local.conferenceId)}`;
    }
    if (hasPassword && local.joinCode) {
      copyMsg += `\n암호 : ${local.joinCode}`;
    }
    try {
      await navigator.clipboard.writeText(copyMsg);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = copyMsg;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    dispatch(toastActions.showSnackbar({ type: 'success', message: '회의 정보가 복사되었습니다.' }));
  }, [conferenceTitle, hasPassword, local.conferenceId, local.joinCode, formatConferenceId, dispatch]);

  // 외부 클릭 시 설정 드롭다운 닫기
  useEffect(() => {
    if (!isSettingOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (settingRef.current && !settingRef.current.contains(e.target as Node)) {
        setIsSettingOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSettingOpen]);

  const participantList = useMemo(
    () => participants.participantIds
      .map((id) => participants.participants[id])
      .filter((p) => !p.isHidden && !p.isWhiteboard && !p.properties?.features_jigasi),
    [participants.participantIds, participants.participants],
  );

  // ─── 정렬 ───────────────────────────────────────────────────────
  const sortedParticipants = useMemo(() => {
    const list = [...participantList];
    const getMasterPriority = (p: Participant) =>
      isMasterUser(masterUsers, p.wehagoId) ? 0 : 1;

    if (screenSort === 'SPEAKER') {
      list.sort((a, b) => {
        const ma = getMasterPriority(a);
        const mb = getMasterPriority(b);
        if (ma !== mb) return ma - mb;
        const ai = speakerOrder.indexOf(a.id);
        const bi = speakerOrder.indexOf(b.id);
        const aSpeaker = ai !== -1 ? ai : Infinity;
        const bSpeaker = bi !== -1 ? bi : Infinity;
        return aSpeaker - bSpeaker;
      });
    } else if (screenSort === 'NAME') {
      list.sort((a, b) => {
        const ma = getMasterPriority(a);
        const mb = getMasterPriority(b);
        if (ma !== mb) return ma - mb;
        const nameA = a.displayName || a.nickname || '';
        const nameB = b.displayName || b.nickname || '';
        return nameA.localeCompare(nameB, 'ko', { numeric: true });
      });
    }
    // AUTO: 기본 participantIds 순서 유지 (마스터 먼저는 별도 처리 안 함 — 입장순)
    return list;
  }, [participantList, screenSort, speakerOrder, masterUsers]);

  const totalCount = participantList.length + 1; // +1 for local

  // ─── 초대된 참석자 목록 조회 ──────────────────────────────────────
  const invitedParticipantVersion = useAppSelector((s) => s.ui.invitedParticipantVersion);

  useEffect(() => {
    if (!local.roomId) return;
    let cancelled = false;
    apiManager.getInvitedParticipantList(local.roomId)
      .then((result) => {
        if (!cancelled) {
          setInvitedParticipants(result.participantList ?? []);
          setResentSet(new Set()); // 목록 새로고침 시 재전송 상태 초기화
        }
      })
      .catch(() => { /* 조회 실패 시 무시 */ });
    return () => { cancelled = true; };
  }, [local.roomId, invitedParticipantVersion]);

  // ─── 참석 중인 사용자 식별자 Set 구성 (초대 목록 필터링용) ────────────
  // userNo/portalId만 사용: WEHAGO 회원 식별자로 USER 타입 초대와 매칭.
  // userContact/userEmail은 회원 프로필 연락처이므로 PHONE/EMAIL 초대 매칭에 사용하면
  // 오탐(회원의 프로필 전화번호 = 초대된 외부참여자 전화번호)이 발생한다.
  const attendingIdentifiers = useMemo(() => {
    const ids = new Set<string>();
    // 로컬 사용자
    if (local.userNo) ids.add(`userNo:${local.userNo}`);
    if (local.wehagoId) ids.add(`portalId:${local.wehagoId}`);
    // 원격 참여자
    for (const p of participantList) {
      if (p.userNo) ids.add(`userNo:${p.userNo}`);
      if (p.wehagoId) ids.add(`portalId:${p.wehagoId}`);
    }
    return ids;
  }, [local.userNo, local.wehagoId, participantList]);

  // ─── 초대 목록에서 이미 참석 중인 사용자 제외 ─────────────────────
  // USER 타입만 userNo/portalId로 매칭. PHONE/EMAIL 타입은 Jitsi 참여자 속성에
  // 입장 방식(전화/이메일)이 노출되지 않아 신뢰할 수 있는 매칭이 불가능하다.
  const filteredInvitedParticipants = useMemo(() => {
    return invitedParticipants.filter((inv) => {
      if (inv.type === 'USER') {
        if (inv.userNo && attendingIdentifiers.has(`userNo:${inv.userNo}`)) return false;
        if (inv.portalId && attendingIdentifiers.has(`portalId:${inv.portalId}`)) return false;
      }
      return true;
    });
  }, [invitedParticipants, attendingIdentifiers]);

  // ─── 참석자/초대자 검색 필터링 ────────────────────────────────────
  const localDisplayName = (local.userName || local.userId || '').toLowerCase();
  const isLocalMatched = !searchTerm || localDisplayName.includes(searchTerm.toLowerCase());

  const filteredAttending = useMemo(() => {
    if (!searchTerm) return sortedParticipants;
    const term = searchTerm.toLowerCase();
    return sortedParticipants.filter((p) => {
      const name = (p.displayName || p.nickname || '').toLowerCase();
      return name.includes(term);
    });
  }, [sortedParticipants, searchTerm]);

  const filteredInvited = useMemo(() => {
    if (!searchTerm) return filteredInvitedParticipants;
    const term = searchTerm.toLowerCase();
    return filteredInvitedParticipants.filter((inv) => {
      const name = inv.type === 'USER'
        ? (inv.userName || inv.portalId || '')
        : inv.type === 'PHONE'
          ? (inv.phone || '')
          : (inv.email || '');
      return name.toLowerCase().includes(term);
    });
  }, [filteredInvitedParticipants, searchTerm]);

  // ─── 참여 알림 재전송 핸들러 ──────────────────────────────────────
  const handleResendNotification = useCallback(async (accessUserNo: number) => {
    if (!local.roomId) return;
    setResendingSet((prev) => new Set(prev).add(accessUserNo));
    try {
      await apiManager.resendParticipantNotification(local.roomId, [accessUserNo]);
      setResentSet((prev) => new Set(prev).add(accessUserNo));
      dispatch(toastActions.showSnackbar({ type: 'success', message: '참여 알림을 재전송했습니다.' }));
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { resultData?: { errorCode?: string } } } };
      const errorCode = axiosErr?.response?.data?.resultData?.errorCode;
      if (errorCode === 'IVE003') {
        setResentSet((prev) => new Set(prev).add(accessUserNo));
        dispatch(toastActions.showSnackbar({ type: 'error', message: '재전송 횟수를 초과했습니다.' }));
      } else if (errorCode === 'IVE004') {
        dispatch(toastActions.showSnackbar({ type: 'info', message: '해당 참석자는 이미 참석 중입니다.' }));
      } else {
        dispatch(toastActions.showSnackbar({ type: 'error', message: '알림 재전송에 실패했습니다.' }));
      }
    } finally {
      setResendingSet((prev) => {
        const next = new Set(prev);
        next.delete(accessUserNo);
        return next;
      });
    }
  }, [local.roomId, dispatch]);

  const handleSelectMain = useCallback((userId: string) => {
    dispatch(mainUserActions.pinUser(userId));
    dispatch(uiActions.setViewMode('focus'));
  }, [dispatch]);

  // ─── 마스터 일괄 제어 콜백 ──────────────────────────────────────
  const handleMuteAllVideo = useCallback(() => {
    const allMuted = participantList.every((p) => p.tracks.videoMuted);
    if (allMuted) {
      dispatch(toastActions.showSnackbar({ type: 'info', message: '모든 참석자의 카메라가 이미 꺼져있습니다.' }));
    } else {
      getConferenceManager()?.muteAllVideo();
      dispatch(toastActions.showSnackbar({ type: 'info', message: '모든 참석자의 카메라를 껐습니다.' }));
    }
    setIsSettingOpen(false);
  }, [participantList, dispatch]);

  const handleRequestUnmuteAllVideo = useCallback(() => {
    getConferenceManager()?.requestUnmuteAll('video');
    dispatch(toastActions.showSnackbar({ type: 'info', message: '카메라 켜기 요청을 보냈습니다.' }));
    setIsSettingOpen(false);
  }, [dispatch]);

  const handleMuteAllAudio = useCallback(() => {
    const allMuted = participantList.every((p) => p.tracks.audioMuted);
    if (allMuted) {
      dispatch(toastActions.showSnackbar({ type: 'info', message: '모든 참석자의 마이크가 이미 꺼져있습니다.' }));
    } else {
      getConferenceManager()?.muteAllAudio();
      dispatch(toastActions.showSnackbar({ type: 'info', message: '모든 참석자의 마이크를 껐습니다.' }));
    }
    setIsSettingOpen(false);
  }, [participantList, dispatch]);

  const handleRequestUnmuteAllAudio = useCallback(() => {
    getConferenceManager()?.requestUnmuteAll('audio');
    dispatch(toastActions.showSnackbar({ type: 'info', message: '마이크 켜기 요청을 보냈습니다.' }));
    setIsSettingOpen(false);
  }, [dispatch]);

  const handleSortChange = useCallback((sort: 'AUTO' | 'SPEAKER' | 'NAME') => {
    dispatch(uiActions.setScreenSort(sort));
    setIsSettingOpen(false);
  }, [dispatch]);

  return (
    <div className="new_chat typeMemberList">
      {/* v1: .chat_tabs 헤더 */}
      <div className="chat_tabs">
        <div className="chat_selection">
          <h2>화상회의 참여자 <em>{totalCount}</em></h2>
        </div>
        <div className="right_btns">
          {/* 설정 드롭다운 (마스터 전용) */}
          {isLocalMaster && (
            <div ref={settingRef} className={`right_btns__item${isSettingOpen ? ' open' : ''}`}>
              <button
                type="button"
                className="memberList__btn"
                onClick={() => setIsSettingOpen((v) => !v)}
              >
                설정
              </button>
              <div className="viewerHeader__layer">
                <strong className="viewerHeader__layerTitle">참석자 제어</strong>
                <ul className="viewerHeader__layerList">
                  <li>
                    <button type="button" className="viewerHeader__layerListBtn" onClick={handleMuteAllVideo}>
                      모두 카메라 끄기
                    </button>
                  </li>
                  <li>
                    <button type="button" className="viewerHeader__layerListBtn" onClick={handleRequestUnmuteAllVideo}>
                      모두 카메라 켜기 요청
                    </button>
                  </li>
                  <li>
                    <button type="button" className="viewerHeader__layerListBtn" onClick={handleMuteAllAudio}>
                      모두 마이크 끄기
                    </button>
                  </li>
                  <li>
                    <button type="button" className="viewerHeader__layerListBtn" onClick={handleRequestUnmuteAllAudio}>
                      모두 마이크 켜기 요청
                    </button>
                  </li>
                </ul>
                <strong className="viewerHeader__layerTitle bar">정렬 기준</strong>
                <ul className="viewerHeader__layerList">
                  <li>
                    <button
                      type="button"
                      className={`viewerHeader__layerListBtn${screenSort === 'AUTO' ? ' selected' : ''}`}
                      onClick={() => handleSortChange('AUTO')}
                    >
                      자동 정렬
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className={`viewerHeader__layerListBtn${screenSort === 'SPEAKER' ? ' selected' : ''}`}
                      onClick={() => handleSortChange('SPEAKER')}
                    >
                      발언자 우선 정렬
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className={`viewerHeader__layerListBtn${screenSort === 'NAME' ? ' selected' : ''}`}
                      onClick={() => handleSortChange('NAME')}
                    >
                      참여자명 정렬
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}
          {!local.isGuest && (
            <button
              type="button"
              className="memberList__btn iconInvite"
              onClick={() => dispatch(uiActions.setSidebarContent('invite'))}
            >
              초대
            </button>
          )}
          {local.isGuest && (
            <div ref={guestInviteRef} className="meetingInfo" style={{ position: 'relative' }}>
              <button
                type="button"
                className="memberList__btn iconInvite"
                onClick={() => setIsGuestInviteInfoOpen((v) => !v)}
              >
                초대하기
              </button>
              {isGuestInviteInfoOpen && (
                <div
                  className="viewerHeader__layer open"
                  style={{ left: 'auto', right: 0, marginTop: 4 }}
                >
                  <div className="memberList_inviteLayer">
                    <strong title={conferenceTitle}>{conferenceTitle}</strong>
                    <dl className="memberList_inviteLayer__dl">
                      {local.conferenceId && (
                        <div>
                          <dt>회의 ID</dt>
                          <dd>{formatConferenceId(local.conferenceId)}</dd>
                        </div>
                      )}
                      {hasPassword && local.joinCode && (
                        <div>
                          <dt>암호</dt>
                          <dd>{local.joinCode}</dd>
                        </div>
                      )}
                    </dl>
                    <div className="memberList_inviteLayer__footer">
                      <button
                        type="button"
                        className="common_round_btn blue3"
                        onClick={handleCopyConferenceInfo}
                      >
                        회의 정보 복사
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="chat_closeBtn"
            onClick={() => dispatch(uiActions.setOpenSideBar(false))}
          >
            <span className="blind">닫기</span>
          </button>
        </div>
      </div>

      <div className="rtcNew_joinMemberList_searchBox">
        <div className="common_round_searchbox4 notosans_font typeBlack">
          {/* 검색취소 버튼 보일때 .showCancel 추가 */}
          <div className={`common_round_searchbox4__flexWrap${searchTerm ? ' showCancel' : ''}`}>
            <input
              type="text"
              className="textinput"
              placeholder="참석자 검색"
              title="검색어 입력"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              className="input_cancelbtn"
              onClick={() => setSearchTerm('')}
            >
              <span className="blind">검색취소</span>
            </button>
            <button type="button" className="input_searchbtn"><span className="blind">검색</span></button>
          </div>
        </div>
      </div>

      {/* 참석 요청 패널 (마스터 전용, MASTER_APPROVAL) */}
      {isLocalMaster && requestJoinUsers.length > 0 && (
        <div className="rtcNew_joinMemberComboBox">
          <div className="comboBox__item">
            <strong className="comboBox__itemText"><em>회의 참여 요청</em>이 있어요!</strong>
            <div className="comboBox__btns">
              <button
                type="button"
                className="rtcNew_joinMemberComboBox__btn"
                onClick={() => requestJoinUsers.forEach((u) => onResponseJoinRequest?.(u.user_identify, 'deny'))}
              >
                전체 거절
              </button>
              <button
                type="button"
                className="rtcNew_joinMemberComboBox__btn blue"
                onClick={() => requestJoinUsers.forEach((u) => onResponseJoinRequest?.(u.user_identify, 'allow'))}
              >
                전체 승인
              </button>
            </div>
          </div>
          {requestJoinUsers.map((user) => (
            <div key={user.user_identify} className="comboBox__item">
              <p className="comboBox__itemText ellipsis">{user.user_name}</p>
              <div className="comboBox__btns">
                <button
                  type="button"
                  className="rtcNew_joinMemberComboBox__btn"
                  onClick={() => onResponseJoinRequest?.(user.user_identify, 'deny')}
                >
                  거절
                </button>
                <button
                  type="button"
                  className="rtcNew_joinMemberComboBox__btn blueline"
                  onClick={() => onResponseJoinRequest?.(user.user_identify, 'allow')}
                >
                  승인
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* v1: .rtcNew_joinMemberListWrap.scrollCustom > ul.rtcNew_joinMemberList */}
      <div className="rtcNew_joinMemberListWrap scrollCustom">
        <ul className="rtcNew_joinMemberList">
          {/* 로컬 사용자 */}
          {isLocalMatched && (
            <ParticipantItem
              participant="local"
              localUserId={localUserId}
              isMainUser={mainUser.mainUserId === null || mainUser.mainUserId === localUserId}
              onSelectMain={handleSelectMain}
              isLocalMaster={isLocalMaster}
              onToggleAudio={onToggleAudio}
              onToggleVideo={onToggleVideo}
            />
          )}

          {/* 원격 참여자 */}
          {filteredAttending.map((p) => (
            <ParticipantItem
              key={p.id}
              participant={p}
              localUserId={localUserId}
              isMainUser={mainUser.mainUserId === p.id}
              onSelectMain={handleSelectMain}
              onKickParticipant={onKickParticipant}
              onGrantMaster={onGrantMaster}
              onClearMaster={onClearMaster}
              isLocalMaster={isLocalMaster}
              onToggleAudio={onToggleAudio}
              onToggleVideo={onToggleVideo}
            />
          ))}

          {/* 초대된 참석자 (미참석) */}
          {filteredInvited.map((inv) => (
            <InvitedParticipantItem
              key={inv.accessUserNo}
              participant={inv}
              onResend={handleResendNotification}
              resendingSet={resendingSet}
              resentSet={resentSet}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ParticipantList;
